import { useEffect, useMemo, useState } from 'react';
import { Object3D } from 'three';

import { Collider } from '../core/index.js';
import { ColliderNode } from '../types/index.js';

import { useStore } from './useStore.js';

export const useCollider = (scene: Object3D, computeCollisions: boolean = false) => {
  const [collisions, setCollisions] = useState<ColliderNode[]>([]);
  const [posY, setPosY] = useState<number | null>(null);
  const [ref, setRef] = useState<ColliderNode | null>(null);
  const world = useStore(({ world }) => world);

  const collider = useMemo(() => {
    return ref ? new Collider(ref) : null;
  }, [ref]);

  useEffect(() => {
    if (computeCollisions) {
      collider?.generate(scene);
    }
  }, [collider, computeCollisions, scene]);

  useEffect(() => {
    const root = ref?.root;
    if (root && collider && world && computeCollisions) {
      const checkCollisionListener = () => {
        const result = collider.getCollisions(world);
        setCollisions(result?.collisions ?? []);
        setPosY(result?.positionY ?? null);
      };
      const flushCollisionListener = () => setCollisions([]);

      root.on('checkCollisions', checkCollisionListener);
      root.on('flushCollisions', flushCollisionListener);

      return () => {
        root.off('checkCollisions', checkCollisionListener);
        root.off('flushCollisions', flushCollisionListener);
      };
    }
  }, [collider, computeCollisions, ref?.root, world]);

  return { collider, collisions, setRef, scene, posY };
};
